import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

const TermsAndConditions = () => {
  const lastUpdated = 'March 10, 2026';

  return (
    <>
      <Helmet>
        <title>Terms & Conditions | SERPfinity</title>
        <meta name="description" content="Review the SERPfinity Terms and Conditions governing use of our AI-powered search visibility platform, subscription plans, and related SEO analysis services." />
            <meta property="og:title" content="Terms & Conditions | SERPfinity" />
            <meta property="og:description" content="Review the SERPfinity Terms and Conditions governing use of our AI-powered search visibility platform, subscription plans, and related SEO analysis services." />
            <meta property="og:type" content="website" />
            <meta property="og:url" content="https://app.serpfinity.ai/terms" />
            <meta property="og:image" content="https://app.serpfinity.ai/og-image.png" />
            <meta property="og:site_name" content="SERPfinity" />
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content="Terms & Conditions | SERPfinity" />
            <meta name="twitter:description" content="Review the SERPfinity Terms and Conditions governing use of our AI-powered search visibility platform." />
            <meta name="twitter:image" content="https://app.serpfinity.ai/og-image.png" />

      </Helmet>
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms & Conditions</h1>
          <p className="text-lg text-blue-200">
            Please read these terms carefully before using SERPfinity.
          </p>
          <p className="text-sm text-blue-300 mt-4">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">1. Acceptance of Terms</h2>
          <p className="text-slate-600 leading-relaxed">
            By accessing or using the SERPfinity platform ("Service"), you agree to be bound by these Terms & Conditions ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms apply to all visitors, users, and others who access or use the Service.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Service after any changes constitutes acceptance of the new Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">2. Description of Service</h2>
          <p className="text-slate-600 leading-relaxed">
            SERPfinity is an AI-powered search visibility platform that provides SEO analysis, AI search visibility tracking, content optimization, keyword research, competitor analysis, backlink exploration, rank tracking, and related digital marketing tools. The Service is provided on a subscription basis with various plan tiers.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">3. User Accounts</h2>
          <p className="text-slate-600 leading-relaxed">
            To access certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">4. Subscription Plans & Billing</h2>
          <p className="text-slate-600 leading-relaxed">
            SERPfinity offers various subscription plans, including free and paid tiers. Paid plans are billed on a recurring basis (monthly or annually) depending on the plan selected. By subscribing to a paid plan, you authorize us to charge your payment method on a recurring basis.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You may cancel your subscription at any time. Cancellation will take effect at the end of the current billing period. No refunds will be provided for partial billing periods unless required by applicable law.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">5. Acceptable Use</h2>
          <p className="text-slate-600 leading-relaxed">You agree not to use the Service to:</p>
          <ul className="list-disc pl-6 text-slate-600 mt-2 space-y-1">
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon the rights of others, including intellectual property rights</li>
            <li>Transmit any harmful, threatening, abusive, or otherwise objectionable content</li>
            <li>Attempt to gain unauthorized access to other systems or networks</li>
            <li>Interfere with or disrupt the integrity or performance of the Service</li>
            <li>Use automated systems (bots, scrapers) to access the Service beyond permitted API usage</li>
            <li>Resell, sublicense, or redistribute the Service without written authorization</li>
            <li>Use the Service for any purpose that is competitive with SERPfinity</li>
          </ul>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">6. Intellectual Property</h2>
          <p className="text-slate-600 leading-relaxed">
            The Service, including its original content, features, and functionality, is owned by SERPfinity and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. Our trademarks may not be used in connection with any product or service without prior written consent.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            You retain ownership of any content you submit through the Service. By submitting content, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, and display such content solely for the purpose of providing the Service.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">7. Data & Privacy</h2>
          <p className="text-slate-600 leading-relaxed">
            Your use of the Service is also governed by our <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</Link>, which describes how we collect, use, and protect your personal information. By using the Service, you consent to the collection and use of data as described in our Privacy Policy.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">8. Service Availability & Modifications</h2>
          <p className="text-slate-600 leading-relaxed">
            We strive to maintain high availability of the Service but do not guarantee uninterrupted access. We may modify, suspend, or discontinue any part of the Service at any time, with or without notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of the Service.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">9. Limitation of Liability</h2>
          <p className="text-slate-600 leading-relaxed">
            To the maximum extent permitted by applicable law, SERPfinity and its affiliates, officers, employees, agents, and partners shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or in connection with your use of the Service.
          </p>
          <p className="text-slate-600 leading-relaxed mt-4">
            Our total liability for any claims arising from or related to the Service shall not exceed the amount you paid to us in the twelve (12) months preceding the event giving rise to the claim.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">10. Disclaimer of Warranties</h2>
          <p className="text-slate-600 leading-relaxed">
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, whether express or implied. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be error-free, secure, or available at all times.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">11. Indemnification</h2>
          <p className="text-slate-600 leading-relaxed">
            You agree to indemnify, defend, and hold harmless SERPfinity and its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney fees, arising out of or in any way connected with your access to or use of the Service or your violation of these Terms.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">12. Termination</h2>
          <p className="text-slate-600 leading-relaxed">
            We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease. All provisions of these Terms which by their nature should survive termination shall survive.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">13. Governing Law</h2>
          <p className="text-slate-600 leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in the United States.
          </p>
        </section>

        <section className="mb-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">14. Contact Us</h2>
          <p className="text-slate-600 leading-relaxed">
            If you have any questions about these Terms & Conditions, please contact us:
          </p>
          <div className="mt-4 p-6 bg-slate-50 rounded-lg">
            <p className="text-slate-700 font-semibold">SERPfinity</p>
            <p className="text-slate-600">Email: <a href="mailto:info@serpfinity.ai" className="text-blue-600 hover:text-blue-800">info@serpfinity.ai</a></p>
            <p className="text-slate-600 mt-2">
              Or visit our <Link to="/contact" className="text-blue-600 hover:text-blue-800 underline">Contact Us</Link> page.
            </p>
          </div>
        </section>

      </div>
    </div>
      </>
  );
};

export default TermsAndConditions;
